import React from 'react';
import { ArrowDown, Linkedin, Github, Mail } from 'lucide-react';
import { usePortfolio } from '../context/PortfolioContext';

const HeroSection = () => {
  const { personalInfo, loading } = usePortfolio();
  const scrollToAbout = () => {
    const element = document.querySelector('#about');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="home"
      className="min-h-screen bg-[#fafbfc] flex items-center relative overflow-hidden"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#2563eb]/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#1e3a5f]/10 rounded-full blur-[150px]" />
      </div>

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(30,58,95,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(30,58,95,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />

      <div className="max-w-[87.5rem] mx-auto px-6 md:px-10 py-32 md:py-40 relative z-10 w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-[#2563eb]/10 border border-[#2563eb]/30 rounded-full px-4 py-2">
              <span className="w-2 h-2 bg-[#2563eb] rounded-full animate-pulse" />
              <span className="text-[#1e3a5f] text-sm font-medium">Available for opportunities</span>
            </div>

            {/* Main Heading */}
            <div className="space-y-4">
              <h1 className="font-black text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight text-[#1e3a5f] whitespace-nowrap">
                {personalInfo.name}
              </h1>
              <p className="text-[#2563eb] text-lg md:text-xl font-semibold uppercase tracking-wider">
                Data Science, AI & Machine Learning Engineer
              </p>
            </div>

            {/* Description */}
            <p className="text-[#64748b] text-lg md:text-xl leading-relaxed max-w-xl">
              {personalInfo.tagline}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <a
                href="#projects"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#projects')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-[#1e3a5f] text-white font-semibold px-8 py-4 rounded-full hover:scale-[1.02] hover:bg-[#2563eb] transition-all duration-300 uppercase tracking-wide text-sm"
              >
                View Projects
              </a>
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="border-2 border-[#1e3a5f] text-[#1e3a5f] font-semibold px-8 py-4 rounded-full hover:bg-[#1e3a5f] hover:text-white transition-all duration-300 uppercase tracking-wide text-sm"
              >
                Get in Touch
              </a>
            </div>

            {/* Social Links */}
            <div className="flex items-center gap-4 pt-4">
              <a
                href={personalInfo.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full border-2 border-[#e2e8f0] flex items-center justify-center text-[#64748b] hover:text-[#2563eb] hover:border-[#2563eb] transition-all duration-300"
              >
                <Linkedin size={20} />
              </a>
              <a
                href={personalInfo.github}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full border-2 border-[#e2e8f0] flex items-center justify-center text-[#64748b] hover:text-[#2563eb] hover:border-[#2563eb] transition-all duration-300"
              >
                <Github size={20} />
              </a>
              <a
                href={`mailto:${personalInfo.email}`}
                className="w-12 h-12 rounded-full border-2 border-[#e2e8f0] flex items-center justify-center text-[#64748b] hover:text-[#2563eb] hover:border-[#2563eb] transition-all duration-300"
              >
                <Mail size={20} />
              </a>
            </div>
          </div>

          {/* Visual Element */}
          <div className="hidden lg:flex justify-center items-center">
            <div className="relative">
              {/* Animated Rings */}
              <div className="w-80 h-80 rounded-full border-2 border-[#2563eb]/20 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-[spin_20s_linear_infinite]" />
              <div className="w-96 h-96 rounded-full border-2 border-[#2563eb]/15 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-[spin_30s_linear_infinite_reverse]" />
              <div className="w-[28rem] h-[28rem] rounded-full border-2 border-[#2563eb]/10 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-[spin_40s_linear_infinite]" />
              
              {/* Center Element */}
              <div className="w-64 h-64 bg-gradient-to-br from-[#1e3a5f] to-[#2563eb] rounded-full flex items-center justify-center relative shadow-2xl shadow-[#1e3a5f]/30">
                <div className="text-center">
                  <span className="text-white text-6xl font-black">AI</span>
                  <p className="text-white/80 text-sm uppercase tracking-widest mt-2">Engineer</p>
                </div>
                {/* Floating Elements */}
                <div className="absolute -top-4 -right-4 bg-[#2563eb] text-white px-3 py-1 rounded-full text-xs font-bold uppercase shadow-lg">
                  ML
                </div>
                <div className="absolute -bottom-4 -left-4 bg-white text-[#1e3a5f] px-3 py-1 rounded-full text-xs font-bold uppercase border-2 border-[#1e3a5f] shadow-lg">
                  NLP
                </div>
                <div className="absolute top-1/2 -right-8 bg-white text-[#2563eb] px-3 py-1 rounded-full text-xs font-bold uppercase border-2 border-[#2563eb] shadow-lg">
                  DL
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div
          onClick={scrollToAbout}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 cursor-pointer animate-bounce"
        >
          <div className="flex flex-col items-center gap-2 text-[#64748b] hover:text-[#2563eb] transition-colors">
            <span className="text-xs uppercase tracking-widest">Scroll</span>
            <ArrowDown size={20} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
